import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { makeStyles } from '@material-ui/core';
import MastercodeUnlockAnnouncement from 'components/@common/Announcements/MastercodeUnlockAnnouncement';
import PageContainer from 'components/@layout/PageContainer';
import GET_LICENSE_DATA from 'graph/orders/get-license-usage-query';
import { ModuleProps } from 'types';
import { Organization } from 'types/schema-generated-types';
import { NumberParam, withDefault, useQueryParams } from 'use-query-params';
import HeaderBar from '../HeaderBar';
import ProductLicenseUsageFilterBar from './components/ProductLicenseUsageFilterBar';
import ProductFamilyTileView from './components/ProductFamilyUsageTileView';

const getRootOrganizationLicenseUsage = (organization?: Organization) => {
  if (!organization?.parentOrganizationId || !organization?.rootOrganization?.licenseUsage) return;
  return organization.rootOrganization.licenseUsage;
};

const AdminProductLicenses: React.FC<ModuleProps> = props => {
  const classes = useStyles();
  const [{ organizationId }, setQuery] = useQueryParams({
    organizationId: withDefault(NumberParam, null)
  });

  const { data, error, loading } = useQuery(GET_LICENSE_DATA, { fetchPolicy: 'network-only' });

  const organizations: Organization[] = data?.organizations ?? [];

  useEffect(() => {
    if (
      organizations.length &&
      (!organizationId || !organizations.some(o => o.id === organizationId))
    ) {
      const rootOrgs = organizations.filter(o => !o.parentOrganizationId);
      // Default to a root organization if possible.
      if (rootOrgs.length) setQuery({ organizationId: rootOrgs[0].id });
      // Otherwise, just use the first in the list.
      else setQuery({ organizationId: organizations[0].id });
    }
  }, [data, organizationId, organizations, setQuery]);

  const handleOrganizationChange = (organizationId: number) => setQuery({ organizationId });

  const organization = organizations.find(o => o.id === organizationId);
  const licenseUsage = organization?.licenseUsage ?? [];
  const rootOrganizationLicenseUsage = getRootOrganizationLicenseUsage(organization);

  return (
    <PageContainer loading={loading} error={error} {...props}>
      <HeaderBar {...props} />
      {organizations.length > 1 && (
        <ProductLicenseUsageFilterBar
          organizations={organizations}
          selectedOrganizationId={organizationId}
          onChange={handleOrganizationChange}
        />
      )}
      <div className={classes.container}>
        <div className={classes.announcementsContainer}>
          <MastercodeUnlockAnnouncement
            href="/admin/products-licenses/orders"
            mastercodes={data?.mastercodes}
          />
        </div>
        {licenseUsage.length > 0 && (
          <div className={classes.productTileContainer}>
            <ProductFamilyTileView
              licenseUsage={licenseUsage}
              rootOrganizationLicenseUsage={rootOrganizationLicenseUsage}
            />
          </div>
        )}
      </div>
    </PageContainer>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    padding: `${theme.spacing(3)}px ${theme.spacing(5)}px`
  },
  announcementsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: `${theme.spacing(4)}px`
  },
  header: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  productTileContainer: {
    paddingBottom: theme.spacing(2)
  }
}));

export default AdminProductLicenses;
